import { useEffect, useRef } from 'react';

export interface KeyboardShortcutOptions {
  /**
   * The key to listen for (e.g., 'k', 'Enter', 'Escape')
   */
  key: string;
  /**
   * Modifier keys that must be pressed
   */
  modifiers?: {
    ctrl?: boolean;
    meta?: boolean;
    alt?: boolean;
    shift?: boolean;
  };
  /**
   * Callback to execute when the shortcut is triggered
   */
  onTrigger: () => void;
  /**
   * Whether the shortcut is enabled (default: true)
   */
  enabled?: boolean;
  /**
   * Prevent default behavior (default: true)
   */
  preventDefault?: boolean;
  /**
   * Stop event propagation (default: false)
   */
  stopPropagation?: boolean;
  /**
   * Whether to ignore shortcuts when typing in input fields (default: true)
   */
  ignoreInputFields?: boolean;
}

/**
 * Hook for registering global keyboard shortcuts
 *
 * @example
 * // Command/Ctrl + K
 * useKeyboardShortcut({
 *   key: 'k',
 *   modifiers: { ctrl: true, meta: true },
 *   onTrigger: () => console.log('Shortcut triggered!')
 * });
 */
export function useKeyboardShortcut({
  key,
  modifiers = {},
  onTrigger,
  enabled = true,
  preventDefault = true,
  stopPropagation = false,
  ignoreInputFields = true,
}: KeyboardShortcutOptions) {
  const callbackRef = useRef(onTrigger);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Debug logging (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('[useKeyboardShortcut] Key pressed:', {
          key: event.key,
          ctrl: event.ctrlKey,
          meta: event.metaKey,
          alt: event.altKey,
          shift: event.shiftKey,
          target: (event.target as HTMLElement).tagName,
        });
      }

      // Check if we should ignore this event (typing in input/textarea)
      if (ignoreInputFields) {
        const target = event.target as HTMLElement;
        const isInputField =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable;

        // Exception: Allow the shortcut if it's a command palette trigger (Cmd/Ctrl + K)
        const isCommandPaletteTrigger = key.toLowerCase() === 'k' && (modifiers.ctrl || modifiers.meta);

        if (isInputField && !isCommandPaletteTrigger) {
          return;
        }
      }

      // Check if the key matches
      const keyMatches = event.key.toLowerCase() === key.toLowerCase();
      if (!keyMatches) return;

      // Check modifier keys
      const ctrlMatch = modifiers.ctrl ? event.ctrlKey : !event.ctrlKey;
      const metaMatch = modifiers.meta ? event.metaKey : !event.metaKey;
      const altMatch = modifiers.alt ? event.altKey : !event.altKey;
      const shiftMatch = modifiers.shift ? event.shiftKey : !event.shiftKey;

      // On Windows/Linux, treat Ctrl as the primary modifier
      // On Mac, treat Meta (Cmd) as the primary modifier
      const isPrimaryModifier =
        (modifiers.ctrl && event.ctrlKey) ||
        (modifiers.meta && event.metaKey);

      // For shortcuts with ctrl or meta, we want either one to work
      const modifierMatch = modifiers.ctrl || modifiers.meta
        ? isPrimaryModifier && altMatch && shiftMatch
        : ctrlMatch && metaMatch && altMatch && shiftMatch;

      if (!modifierMatch) return;

      // Debug logging (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('[useKeyboardShortcut] Shortcut triggered!', { key, modifiers });
      }

      // Trigger the callback
      if (preventDefault) {
        event.preventDefault();
      }
      if (stopPropagation) {
        event.stopPropagation();
      }

      callbackRef.current();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, modifiers, enabled, preventDefault, stopPropagation, ignoreInputFields]);
}

/**
 * Get a human-readable shortcut string for display
 *
 * @example
 * getShortcutString('k', { ctrl: true, meta: true }) // Returns "⌘K" on Mac, "Ctrl+K" on Windows
 */
export function getShortcutString(
  key: string,
  modifiers: { ctrl?: boolean; meta?: boolean; alt?: boolean; shift?: boolean } = {}
): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const parts: string[] = [];

  if (modifiers.ctrl && !isMac) parts.push('Ctrl');
  if (modifiers.meta && isMac) parts.push('⌘');
  if (modifiers.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (modifiers.shift) parts.push(isMac ? '⇧' : 'Shift');

  // Capitalize the key
  const keyDisplay = key.length === 1 ? key.toUpperCase() : key;
  parts.push(keyDisplay);

  return isMac ? parts.join('') : parts.join('+');
}
